import { atom, useAtomValue, useSetAtom } from 'jotai';
import { conversationTodoFilterCache } from 'services/cache';
import { Dropdown, Menu } from 'antd';
import { CaretDownOutlined } from '@ant-design/icons';
import { useCreation } from 'ahooks';
import { useTranslate } from '@tolgee/react';

export type TodoFilterType = 'all' | 'unprocessed';

const conversationTodoFilterAtom = atom<TodoFilterType>(
  conversationTodoFilterCache.get<TodoFilterType | undefined>() || 'all',
);
export const conversationTodoFilterState = atom(
  (get) => get(conversationTodoFilterAtom),
  (_, set, next: TodoFilterType) => {
    set(conversationTodoFilterAtom, next);
    conversationTodoFilterCache.set(next);
  },
);

export const useConversationTodoFilter = () => useAtomValue(conversationTodoFilterState);

export const useSetConversationTodoFilter = () => useSetAtom(conversationTodoFilterState);

interface ConversationTodoFilterProps {
  reload: () => void;
  value?: TodoFilterType;
  onChange?: (value: TodoFilterType) => void;
}

export const ConversationTodoFilter = (props: ConversationTodoFilterProps) => {
  const { reload, value: controlledValue, onChange } = props;
  const internalFilter = useConversationTodoFilter();
  const setInternalFilter = useSetConversationTodoFilter();

  // 支持受控和非受控模式
  const isControlled = controlledValue !== undefined;
  const filter = isControlled ? controlledValue : internalFilter;
  const setFilter = isControlled ? onChange! : setInternalFilter;

  const { t } = useTranslate();
  const typeLabelMap = useCreation(() => {
    return {
      all: t('scene.chatWorkspace.taskTab.filter.all'),
      unprocessed: t('scene.chatWorkspace.conversationList.tag.pending'),
    };
  }, [t]);
  return (
    <Dropdown
      overlay={
        <Menu
          activeKey={filter}
          onClick={({ key }) => {
            if (!isControlled) {
              reload();
            }
            setFilter(key as TodoFilterType);
          }}
        >
          {Object.entries(typeLabelMap).map(([type, label]) => {
            return <Menu.Item key={type}>{label}</Menu.Item>;
          })}
        </Menu>
      }
    >
      <div className="flex items-center gap-[2px] text text-xs text-content-200 cursor-pointer hover:text-primary-hover">
        {typeLabelMap[filter]}
        <CaretDownOutlined />
      </div>
    </Dropdown>
  );
};

ConversationTodoFilter.displayName = 'ConversationTodoFilter';
